import { useMemo } from 'react';
import { 
  Box, Typography , TableContainer , Table , TableHead , TableRow , TableCell , TableBody , Paper 
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import DoughnutGraph from 'components/Home/DoughnutGraph';
import VerticalGraph from 'components/VerticalGraph';

const DOUGHNUT_GRAPH_COLORS = ['#009970', '#F5F9FF'];

const AVERAGE_SCORE_LEGENDS = [
  { range: "0-30", color: "#E2483C" },
  { range: "31-60", color: "#F5B847" },
  { range: "61-100", color: "#009970" }
]

const CODING_SCORE_LEGENDS = [
  { range: "0-1.2", color: "#E2483C" },
  { range: "1.2-2.33", color: "#F5B847" },
  { range: "2.34-5.0", color: "#009970" }
]

const useStyles = makeStyles((theme) => ({
  leftSection: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column'
    },
    metricsContainer: {
      display: 'flex',
      gap: '16px',
      width: '100%'
    },
    chartContainer: {
      display: 'flex',
      gap: '70px',
      padding: '20px',
      backgroundColor: '#FCFAFA',
      borderRadius: '12px'
    }
}))

export const AttemptRateChart = ({ attemptRate, totalStudents = 100, isShortlisting = false }) => {
  const classes = useStyles();

  const submittedCount = attemptRate?.userSubmittedCount || 0;
  const notSubmittedCount = attemptRate?.userNotSubmittedCount || 0;
  const totalCount = submittedCount + notSubmittedCount;
  
  const tableRows = useMemo(() => {
    if (isShortlisting) {
      return [
        { type: 'Submitted', students: submittedCount },
        { type: 'Not submitted', students: notSubmittedCount }
      ];
    } else {
      return [
        { type: '0-30%', students: attemptRate?.userNotSubmittedCount || 0 },
        { type: '31-60%', students: Math.floor((attemptRate?.userSubmittedCount || 0) / 2) },
        { type: '61-100%', students: Math.ceil((attemptRate?.userSubmittedCount || 0) / 2) }
      ];
    }
  }, [attemptRate, submittedCount, notSubmittedCount, isShortlisting]);

  const chartData = totalCount > 0 ? [attemptRate?.attemptRate || 0, 100 - (attemptRate?.attemptRate || 0)] : [0, 100];

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" flexDirection="column" gap="12px">
        <Typography variant="h5-medium" color="neutral.clr-700">
          Attempt rate 
        </Typography>
        <Box className={classes.chartContainer}>
          <Box className={classes.leftSection}>
            <DoughnutGraph
              rootStyle={{ padding: 0 }}
              color="#0263FF"
              score={Math.round(attemptRate?.attemptRate || 0)}
              data={chartData}
              labels={["Attempt rate", "Remaining"]}
              title={`of ${totalStudents} students`}
              muiltiProgress={true}
              bgColors={DOUGHNUT_GRAPH_COLORS}
              showTooltip={false}
              isPercentage={true}
            />
          </Box>
          {/* <AttemptRateTable rows={tableRows} isShortlisting={isShortlisting} /> */}
        </Box>
      </Box>
    </Box>
  );
};

export const AttemptRateTable = ({ rows, isShortlisting = false }) => (
  <TableContainer component={Paper} sx={{ maxWidth: 300 }}>
    <Table>
      <TableHead sx={{ backgroundColor: '#f5f9fc' }}>
        <TableRow>
          <TableCell>
            <Typography variant="body01-bold">
              {isShortlisting ? 'Student type' : 'Attempt rate'}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body01-bold">Students</Typography>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.type}>
            <TableCell>
              <Typography variant="body01-medium">{row.type}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body01-medium">{row.students}</Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ScoreDistributionChart  = ({ avgDistribution, scoreDistribution , boxShadow = false, interviewVariantType = 'verbal_mock' , totalStudents = 100 }) => {
  const classes = useStyles();
    const isCodingMock = interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw';
    const legends = isCodingMock ? CODING_SCORE_LEGENDS : AVERAGE_SCORE_LEGENDS;
    const maxScore = isCodingMock ? 5 : 100;
    
    return (
      <Box sx={{ width: '100%'  }}>
        <Box display="flex" flexDirection="column" gap="12px">
          <Typography variant="h5-medium" color="neutral.clr-700">
            Batch average & score distribution
          </Typography>
          <Box className={classes.chartContainer}>
            <Box className={classes.leftSection}>
              <DoughnutGraph
                rootStyle={{ padding: 0 }}
                color="#009970"
                score={isCodingMock ? (avgDistribution?.batchAverageScore || 0).toFixed(2) : Math.round(avgDistribution?.batchAverageScore || 0)}
                data={[
                  avgDistribution?.batchAverageScore || 0,
                  maxScore - (avgDistribution?.batchAverageScore || 0)
                ]}
                labels={["Score", "Remaining"]}
                title={`of ${totalStudents} students`}
                muiltiProgress={true}
                bgColors={DOUGHNUT_GRAPH_COLORS}
                showTooltip={false}
              />
            </Box>
            <VerticalGraph 
              averageScoreStats={scoreDistribution}
              averageScoreLegends={legends}
              xAsix="Score range"
              yAsix="Student count"
              totalStudents={totalStudents}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  export default ScoreDistributionChart;